import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, Skeleton, Alert, Chip, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  useMediaQuery, Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BalanceRounded as BalanceIcon,
  ReceiptLongRounded as ReceiptIcon,
  DescriptionRounded as StatementIcon,
  SearchRounded as ApplyIcon,
  ArticleRounded as SlipsIcon,
  ExpandMoreRounded as ExpandIcon,
  SmartphoneRounded as PayIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import ReceiptsView from './ReceiptsView';
import ReceiptSlips from './ReceiptSlips';
import PortalPayDialog from './PortalPayDialog';
import { P, money, CARD_SHADOW } from './portalTheme';

// ─────────────────────────────────────────────────────────────────
// PortalAccount — everything about the money, behind a side menu.
//
//   Balances    what each fee account stands at right now
//   Receipts    every receipt already issued, openable in full
//   Statement   the running ledger — billed, paid, balance after each
//               line, the same shape the cashier desk reads
//
// The menu is a real side menu on a laptop and a row of pills on a
// phone. Same three destinations either way: a parent who learns this
// on their phone should not meet a different page on a borrowed
// laptop.
// ─────────────────────────────────────────────────────────────────

const SECTIONS = [
  { key: 'balances',  label: 'Balances',  icon: <BalanceIcon   sx={{ fontSize: 20 }} />,
    blurb: 'What you owe now' },
  { key: 'history',   label: 'Payments',  icon: <ReceiptIcon   sx={{ fontSize: 20 }} />,
    blurb: 'Every payment made' },
  // The slips themselves, read like a PDF. Separate from the list
  // above on purpose: one answers "what have I paid", the other is the
  // document you print and hand over.
  { key: 'slips',     label: 'Receipts',  icon: <SlipsIcon     sx={{ fontSize: 20 }} />,
    blurb: 'The printed slips' },
  { key: 'statement', label: 'Statement', icon: <StatementIcon sx={{ fontSize: 20 }} />,
    blurb: 'Billed, paid, balance' },
];

// ── Balances ────────────────────────────────────────────────────
//
// One table, not a stack of cards.
//
// The previous version drew a card per fee account, each repeating the
// same six labelled cells — eighteen labels on screen for three
// accounts, all saying the same six words. That is what made the page
// feel rigid: the labels dominated, and nothing could be compared
// because every figure sat in its own box.
//
// A table prints each label once and puts the accounts on rows, so
// "which account is behind" is answered by running an eye down one
// column. The totals row at the foot is the number the parent came
// for, and it now has somewhere to live.
//
// Phones still get cards, because a seven-column table at 360px is
// unreadable — but the compact kind, with only the figures that matter
// on the face and the rest behind an expander.
function Balances({ data, loading, error, onPay }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState({});

  if (loading) {
    return (
      <Stack spacing={1.4}>
        {[0, 1, 2].map(i => (
          <Skeleton key={i} variant="rounded" height={isDesktop ? 56 : 88} sx={{ borderRadius: 3 }} />
        ))}
      </Stack>
    );
  }
  if (error) return <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>;

  const fees  = data?.fees || [];
  const progs = data?.accounts || [];

  if (!fees.length && !progs.length) {
    return (
      <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center', boxShadow: 'none',
        border: '1px dashed', borderColor: P.line }}>
        <BalanceIcon sx={{ fontSize: 38, color: P.line }} />
        <Typography sx={{ fontWeight: 800, color: P.ink, mt: 1 }}>No accounts assigned</Typography>
        <Typography sx={{ fontSize: 13, color: P.inkSoft, mt: .5 }}>
          The school office sets these up.
        </Typography>
      </Card>
    );
  }

  const tot = fees.reduce((t, f) => ({
    arrears:     t.arrears     + Number(f.arrears || 0),
    term_bill:   t.term_bill   + Number(f.term_bill || 0),
    scholarship: t.scholarship + Number(f.scholarship || 0),
    adjustments: t.adjustments + Number(f.adjustments || 0),
    total_due:   t.total_due   + Number(f.total_due || 0),
    paid:        t.paid        + Number(f.paid || 0),
    balance:     t.balance     + Number(f.balance || 0),
  }), { arrears: 0, term_bill: 0, scholarship: 0, adjustments: 0, total_due: 0, paid: 0, balance: 0 });

  // How far through each bill the parent is. Shown as a thin bar under
  // the account name — a percentage is easier to feel than two numbers
  // to subtract.
  const pctPaid = (f) => {
    const due = Number(f.total_due || 0);
    if (due <= 0) return 100;
    return Math.max(0, Math.min(100, (Number(f.paid || 0) / due) * 100));
  };

  const stateOf = (bal) => {
    const v = Number(bal || 0);
    if (Math.abs(v) < 0.005) return { label: 'Settled', bg: '#EEF2F7', fg: P.inkSoft, tone: P.ink };
    if (v > 0)               return { label: 'Owing',   bg: '#FDECEA', fg: P.bad,     tone: P.bad };
    return                          { label: 'Credit',  bg: '#E9F7EE', fg: P.good,    tone: P.good };
  };

  const Money = ({ v, dim, strong, color }) => (
    <Typography component="span" sx={{
      fontSize: strong ? 14 : 13, fontWeight: strong ? 900 : 600,
      color: color || (dim && Math.abs(Number(v || 0)) < 0.005 ? P.inkSoft : P.ink),
      fontVariantNumeric: 'tabular-nums',
    }}>
      {money(v)}
    </Typography>
  );

  const owing = fees.reduce((s, f) => s + Math.max(0, Number(f.balance || 0)), 0);

  return (
    <Stack spacing={2.5}>
      {/* The one action this table implies. It appears only when there
          is something to settle — an ever-present Pay button on a
          cleared account is an invitation to overpay by accident. */}
      {owing > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{
            px: 2, py: 1.6, borderRadius: 3,
            bgcolor: P.brandSoft, border: '1px solid', borderColor: P.line,
          }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: P.ink }}>
              GHS {money(owing)} outstanding
            </Typography>
            <Typography sx={{ fontSize: 12, color: P.inkSoft }}>
              Pay by mobile money — MTN, Telecel or AirtelTigo.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<PayIcon />} onClick={onPay}
            sx={{ fontWeight: 800, borderRadius: 2.5, px: 2.4, py: 1,
              bgcolor: P.brand, whiteSpace: 'nowrap',
              '&:hover': { bgcolor: P.brandDeep } }}>
            Pay now
          </Button>
        </Stack>
      )}

      {fees.length > 0 && (isDesktop ? (
        <Card sx={{
          borderRadius: 3.5, boxShadow: CARD_SHADOW, overflow: 'hidden',
          border: '1px solid', borderColor: P.line,
        }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{
                  '& th': {
                    bgcolor: '#F7FAFE', color: P.inkSoft, fontWeight: 800, fontSize: 10.5,
                    letterSpacing: .5, textTransform: 'uppercase', whiteSpace: 'nowrap',
                    borderBottom: '1px solid', borderColor: P.line, py: 1.2,
                  },
                }}>
                  <TableCell sx={{ minWidth: 190 }}>Fee account</TableCell>
                  <TableCell align="right">Arrears b/f</TableCell>
                  <TableCell align="right">Term bill</TableCell>
                  <TableCell align="right">Scholarship</TableCell>
                  <TableCell align="right">Adjustments</TableCell>
                  <TableCell align="right">Total due</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fees.map(f => {
                  const st = stateOf(f.balance);
                  const pct = pctPaid(f);
                  return (
                    <TableRow key={f.key} hover sx={{ '& td': { py: 1.3, fontSize: 13 } }}>
                      <TableCell>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: P.ink }}>
                          {f.label}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: .5 }}>
                          <Box sx={{
                            flex: 1, height: 5, borderRadius: 3, bgcolor: '#EDF1F7',
                            overflow: 'hidden', maxWidth: 120,
                          }}>
                            <Box sx={{
                              width: `${pct}%`, height: '100%',
                              bgcolor: pct >= 99.5 ? P.good : P.brand,
                              transition: 'width .5s ease',
                            }} />
                          </Box>
                          <Typography sx={{ fontSize: 10.5, color: P.inkSoft, fontWeight: 700 }}>
                            {Math.round(pct)}%
                          </Typography>
                          {(f.term || f.year) && (
                            <Typography sx={{ fontSize: 10.5, color: P.inkSoft }}>
                              · {[f.term, f.year].filter(Boolean).join(' ')}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Money v={f.arrears} dim color={f.arrears > 0 ? P.bad : undefined} />
                      </TableCell>
                      <TableCell align="right"><Money v={f.term_bill} dim /></TableCell>
                      <TableCell align="right">
                        <Money v={f.scholarship} dim color={f.scholarship > 0 ? P.good : undefined} />
                      </TableCell>
                      <TableCell align="right"><Money v={f.adjustments} dim /></TableCell>
                      <TableCell align="right"><Money v={f.total_due} strong /></TableCell>
                      <TableCell align="right"><Money v={f.paid} strong color={P.good} /></TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                          <Chip size="small" label={st.label}
                            sx={{ height: 20, fontWeight: 800, fontSize: 10.5,
                              bgcolor: st.bg, color: st.fg }} />
                          <Money v={Math.abs(Number(f.balance || 0))} strong color={st.tone} />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              {fees.length > 1 && (
                <TableBody>
                  <TableRow sx={{
                    '& td': {
                      py: 1.4, borderTop: '2px solid', borderColor: P.line,
                      bgcolor: '#F7FAFE',
                    },
                  }}>
                    <TableCell>
                      <Typography sx={{ fontSize: 12, fontWeight: 900, letterSpacing: .5,
                        textTransform: 'uppercase', color: P.inkSoft }}>
                        All accounts
                      </Typography>
                    </TableCell>
                    <TableCell align="right"><Money v={tot.arrears} /></TableCell>
                    <TableCell align="right"><Money v={tot.term_bill} /></TableCell>
                    <TableCell align="right"><Money v={tot.scholarship} /></TableCell>
                    <TableCell align="right"><Money v={tot.adjustments} /></TableCell>
                    <TableCell align="right"><Money v={tot.total_due} strong /></TableCell>
                    <TableCell align="right"><Money v={tot.paid} strong color={P.good} /></TableCell>
                    <TableCell align="right">
                      <Money v={Math.abs(tot.balance)} strong color={stateOf(tot.balance).tone} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Card>
      ) : (
        // Phone: the two figures that matter on the face, the rest a tap
        // away. Seven columns will not fit, and shrinking them to fit is
        // how a table becomes unreadable rather than compact.
        <Stack spacing={1.2}>
          {fees.map(f => {
            const st = stateOf(f.balance);
            const pct = pctPaid(f);
            const isOpen = !!open[f.key];
            return (
              <Card key={f.key} sx={{
                borderRadius: 3.5, boxShadow: CARD_SHADOW, overflow: 'hidden',
                border: '1px solid', borderColor: P.line,
              }}>
                <Box onClick={() => setOpen(o => ({ ...o, [f.key]: !o[f.key] }))}
                  sx={{ px: 2, py: 1.6, cursor: 'pointer' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ flex: 1, fontSize: 15, fontWeight: 800, color: P.ink }}>
                      {f.label}
                    </Typography>
                    <Chip size="small" label={st.label}
                      sx={{ height: 20, fontWeight: 800, fontSize: 10.5,
                        bgcolor: st.bg, color: st.fg }} />
                    <Typography sx={{
                      fontSize: 16, fontWeight: 900, color: st.tone,
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {money(Math.abs(Number(f.balance || 0)))}
                    </Typography>
                    <ExpandIcon sx={{
                      fontSize: 20, color: P.inkSoft,
                      transform: isOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform .2s',
                    }} />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                    <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#EDF1F7', overflow: 'hidden' }}>
                      <Box sx={{
                        width: `${pct}%`, height: '100%',
                        bgcolor: pct >= 99.5 ? P.good : P.brand,
                        transition: 'width .5s ease',
                      }} />
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: P.inkSoft }}>
                      {money(f.paid)} of {money(f.total_due)}
                    </Typography>
                  </Stack>
                </Box>
                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{
                    px: 2, py: 1.6, bgcolor: '#F7FAFE',
                    borderTop: '1px solid', borderColor: P.line,
                    display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 1.2, columnGap: 2,
                  }}>
                    {[
                      ['Arrears b/f', f.arrears, f.arrears > 0 ? P.bad : null],
                      ['Term bill',   f.term_bill, null],
                      ['Scholarship', f.scholarship, f.scholarship > 0 ? P.good : null],
                      ['Adjustments', f.adjustments, null],
                      ['Total due',   f.total_due, null],
                      ['Paid',        f.paid, P.good],
                    ].map(([label, v, c]) => (
                      <Stack key={label} direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: 12, color: P.inkSoft }}>{label}</Typography>
                        <Typography sx={{
                          fontSize: 12.5, fontWeight: 700, color: c || P.ink,
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {money(v)}
                        </Typography>
                      </Stack>
                    ))}
                    {(f.term || f.year) && (
                      <Typography sx={{ gridColumn: '1 / -1', fontSize: 11.5, color: P.inkSoft }}>
                        {[f.term, f.year].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Card>
            );
          })}
        </Stack>
      ))}

      {progs.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: .6,
            textTransform: 'uppercase', color: P.inkSoft, mb: 1.2 }}>
            Daily programmes
          </Typography>
          <Box sx={{
            display: 'grid', gap: 1.2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
          }}>
            {progs.map(a => {
              const owing = Number(a.arrears || 0) - Number(a.credit || 0);
              const st = stateOf(owing);
              return (
                <Card key={a.key} sx={{
                  borderRadius: 3.5, px: 2, py: 1.6, boxShadow: CARD_SHADOW,
                  border: '1px solid', borderColor: P.line,
                }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 14.5, fontWeight: 800, color: P.ink }}>
                        {a.label}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: P.inkSoft }}>
                        GHS {money(a.daily_amount)} a day
                        {a.days_owed > 0 && ` · ${a.days_owed} day${a.days_owed === 1 ? '' : 's'} behind`}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{
                        fontSize: 16, fontWeight: 900, color: st.tone,
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {money(Math.abs(owing))}
                      </Typography>
                      <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: .4,
                        textTransform: 'uppercase', color: st.fg }}>
                        {st.label}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}
    </Stack>
  );
}

// ── Statement ───────────────────────────────────────────────────
function Statement() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [st, setSt]           = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  // Default window: a year back. Wide enough to hold a full academic
  // year without the parent touching anything.
  const today = new Date();
  const yearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
  const d10 = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [form, setForm] = useState({ from: d10(yearAgo), to: d10(today), account: '' });
  const [applied, setApplied] = useState({ from: d10(yearAgo), to: d10(today), account: '' });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    API.get('/portal/statement', { params: applied })
      .then(r => { if (alive) { setSt(r.data); setError(''); } })
      .catch(e => { if (alive) setError(e?.response?.data?.message || 'Could not load your statement'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [applied]);

  const t = st?.totals || { billed: 0, paid: 0, balance: 0, entries: 0 };

  return (
    <Box>
      <Card sx={{ borderRadius: 3.5, p: 2, mb: 2, boxShadow: CARD_SHADOW,
        border: '1px solid', borderColor: P.line }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ sm: 'center' }}>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value }))}
            sx={{ flex: 1 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))}
            sx={{ flex: 1 }} />
          <TextField size="small" select SelectProps={{ native: true }} label="Account"
            InputLabelProps={{ shrink: true }}
            value={form.account} onChange={e => setForm(f => ({ ...f, account: e.target.value }))}
            sx={{ flex: 1 }}>
            <option value="">All accounts</option>
            {(st?.accounts || []).map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
          </TextField>
          <Button variant="contained" startIcon={<ApplyIcon />} onClick={() => setApplied(form)}
            sx={{ fontWeight: 800, textTransform: 'none', bgcolor: P.brandDeep,
              '&:hover': { bgcolor: '#0A3A85' } }}>
            Apply
          </Button>
        </Stack>
      </Card>

      <Stack direction="row" spacing={1.4} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {[
          { k: 'Billed', v: t.billed, c: P.ink },
          { k: 'Paid',   v: t.paid,   c: P.good },
          { k: t.balance > 0 ? 'Still owing' : 'Balance', v: Math.abs(t.balance),
            c: t.balance > 0 ? P.bad : P.ink },
        ].map(s => (
          <Card key={s.k} sx={{
            flex: '1 1 140px', borderRadius: 3.5, px: 2, py: 1.4, boxShadow: CARD_SHADOW,
            border: '1px solid', borderColor: P.line,
          }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: .5,
              textTransform: 'uppercase', color: P.inkSoft }}>{s.k}</Typography>
            <Typography sx={{ fontSize: 19, fontWeight: 900, color: s.c,
              fontVariantNumeric: 'tabular-nums' }}>
              GHS {money(s.v)}
            </Typography>
          </Card>
        ))}
      </Stack>

      {error && <Alert severity="error" sx={{ borderRadius: 3, mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack spacing={1}>
          {[0, 1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={46} sx={{ borderRadius: 2 }} />)}
        </Stack>
      ) : !st?.entries?.length ? (
        <Card sx={{ borderRadius: 3, p: 4, textAlign: 'center', boxShadow: 'none',
          border: '1px dashed', borderColor: P.line }}>
          <StatementIcon sx={{ fontSize: 38, color: P.line }} />
          <Typography sx={{ fontWeight: 800, color: P.ink, mt: 1 }}>
            Nothing in this period
          </Typography>
          <Typography sx={{ fontSize: 13, color: P.inkSoft, mt: .5 }}>
            Widen the dates above to see more.
          </Typography>
        </Card>
      ) : isDesktop ? (
        <Card sx={{ borderRadius: 3.5, boxShadow: CARD_SHADOW, border: '1px solid', borderColor: P.line }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{
                  '& th': {
                    bgcolor: '#FAFBFC', color: P.inkSoft, fontWeight: 800, fontSize: 11,
                    letterSpacing: .5, textTransform: 'uppercase',
                    borderBottom: '1px solid', borderColor: P.line,
                  },
                }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {st.entries.map(e => {
                  const rev = e.status === 'REVERSED';
                  return (
                    <TableRow key={e.id} hover sx={{
                      '& td': { fontSize: 12.5, py: 1, opacity: rev ? .5 : 1 },
                    }}>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {e.date}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11.5 }}>
                        {e.receipt_no || '—'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{e.account}</TableCell>
                      <TableCell sx={{ color: P.inkSoft, maxWidth: 240, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.description}
                        {rev && <Chip size="small" label="reversed" sx={{ ml: 1, height: 17, fontSize: 9.5 }} />}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {e.debit ? money(e.debit) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{
                        fontVariantNumeric: 'tabular-nums', color: e.credit ? P.good : 'inherit',
                        fontWeight: e.credit ? 700 : 400,
                      }}>
                        {e.credit ? money(e.credit) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{
                        fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                        color: e.balance > 0 ? P.bad : P.ink,
                      }}>
                        {money(Math.abs(e.balance))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        <Stack spacing={1}>
          {st.entries.map(e => (
            <Card key={e.id} sx={{
              borderRadius: 3.5, px: 1.8, py: 1.4, boxShadow: CARD_SHADOW,
              border: '1px solid', borderColor: P.line,
              opacity: e.status === 'REVERSED' ? .55 : 1,
            }}>
              <Stack direction="row" alignItems="flex-start" spacing={1.2}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 700, color: P.ink }}>
                    {e.account}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 11.5, color: P.inkSoft }}>
                    {e.date}{e.receipt_no ? ` · ${e.receipt_no}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{
                    fontSize: 13.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                    color: e.credit ? P.good : P.ink,
                  }}>
                    {e.credit ? `+${money(e.credit)}` : money(e.debit)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: P.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
                    bal {money(Math.abs(e.balance))}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      {!loading && st?.entries?.length > 0 && (
        <Typography sx={{ mt: 2, textAlign: 'center', fontSize: 12, color: P.inkSoft }}>
          {t.entries} entr{t.entries === 1 ? 'y' : 'ies'} · {applied.from} to {applied.to}
        </Typography>
      )}
    </Box>
  );
}

// ── The page ────────────────────────────────────────────────────
export default function PortalAccount() {
  // No breakpoint hook here any more: the tab strip is one shape at
  // every width, and the sections that do care about width ask for
  // themselves.
  const [section, setSection] = useState('balances');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    API.get('/portal/me')
      .then(r => { if (alive) { setData(r.data); setError(''); } })
      .catch(e => { if (alive) setError(e?.response?.data?.message || 'Could not load your account'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const reload = () => { API.get('/portal/me').then(r => setData(r.data)).catch(() => {}); };

  const owing = data?.summary?.owing || 0;
  const student = useMemo(() => data?.student || null, [data]);

  // ── The section switcher ───────────────────────────────────────
  //
  // A bordered tab strip across the top, not a column of cards down the
  // left. Two reasons it changed:
  //
  //   The four cards read as CONTENT — boxes with a title and a
  //   subtitle, sitting beside more boxes with titles and subtitles, so
  //   nothing said "these are the controls and those are the figures".
  //   A tab strip is unmistakably a switcher.
  //
  //   It also cost 210px of width on every screen, permanently, to show
  //   four words. Moving it up gives the table underneath the whole
  //   page — which is what a seven-column table needs.
  //
  // One shape at every width; it simply scrolls sideways on a phone
  // rather than becoming a different control.
  const menu = (
    <Box sx={{
      display: 'flex', mb: 2.5,
      bgcolor: P.card, borderRadius: 3,
      border: '1px solid', borderColor: P.line,
      boxShadow: CARD_SHADOW,
      overflowX: 'auto',
      '&::-webkit-scrollbar': { height: 5 },
    }}>
      {SECTIONS.map((s, i) => {
        const on = section === s.key;
        return (
          <Box key={s.key} onClick={() => setSection(s.key)}
            sx={{
              position: 'relative', flex: { xs: '0 0 auto', md: 1 }, minWidth: 0,
              px: { xs: 2, md: 2.2 }, py: 1.4, cursor: 'pointer',
              borderLeft: i === 0 ? 'none' : '1px solid',
              borderColor: P.line,
              bgcolor: on ? P.brandSoft : 'transparent',
              transition: 'background-color .18s',
              '&:hover': { bgcolor: on ? P.brandSoft : P.brandFaint },
              // The active tab is underlined at its foot, the way a tab
              // strip is read. Colour alone would make it look merely
              // hovered.
              '&::after': on ? {
                content: '""', position: 'absolute', left: 0, right: 0, bottom: 0,
                height: 3, bgcolor: P.brand,
              } : {},
            }}>
            <Stack direction="row" alignItems="center" spacing={1.2}>
              <Box sx={{ display: 'flex', color: on ? P.brand : P.inkSoft }}>{s.icon}</Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{
                  fontSize: 14, fontWeight: on ? 800 : 700,
                  color: on ? P.brandDeep : P.ink, lineHeight: 1.25,
                }}>
                  {s.label}
                </Typography>
                <Typography noWrap sx={{
                  fontSize: 11, color: P.inkSoft,
                  display: { xs: 'none', sm: 'block' },
                }}>
                  {s.blurb}
                </Typography>
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box>
      {/* One line of context, so a parent flipping between sections
          never loses which child and what the total is. */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 19, md: 22 }, fontWeight: 800, color: P.ink }}>
            Accounts
          </Typography>
          {student && (
            <Typography noWrap sx={{ fontSize: 12.5, color: P.inkSoft }}>
              {student.name}{student.cla ? ` · ${student.cla}` : ''} · {student.ind}
            </Typography>
          )}
        </Box>
        {!loading && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: .5,
              textTransform: 'uppercase', color: P.inkSoft }}>
              {owing > 0 ? 'Total owing' : 'Balance'}
            </Typography>
            <Typography sx={{
              fontSize: 20, fontWeight: 900, fontVariantNumeric: 'tabular-nums',
              color: owing > 0 ? P.bad : P.ink,
            }}>
              GHS {money(Math.abs(owing))}
            </Typography>
          </Box>
        )}
      </Stack>

      {menu}

      <Box sx={{ minWidth: 0 }}>
        {section === 'balances'  && (
          <Balances data={data} loading={loading} error={error}
            onPay={() => setPayOpen(true)} />
        )}
        {section === 'history'   && <ReceiptsView student={student} showHeader={false} />}
        {section === 'slips'     && <ReceiptSlips />}
        {section === 'statement' && <Statement />}
      </Box>

      <PortalPayDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        onQueued={reload}
        studentName={student?.name}
      />
    </Box>
  );
}
